import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { AlertTriangle, Search, Wrench, FileText } from 'lucide-react';

const NOCApplicationsSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="Practical applications of reasoning models in Network Operations"
      >
        NOC Applications: Root Cause Analysis
      </SlideTitle>
      
      <SlideContent>
        {/* Alert example */}
        <div className="slide-card mb-6 border-destructive/30">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            <span className="font-heading font-semibold text-foreground">Alert: BGP Route Overflow</span>
            <span className="tech-badge">HIGH SEVERITY</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Border router receiving <span className="text-destructive font-mono">850K</span> BGP routes (normal: ~100K)
          </p>
        </div>
        
        {/* Reasoning chain */}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="slide-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-xs font-mono text-primary">1</span>
                </div>
                <span className="font-heading font-semibold text-foreground">Symptom Analysis</span>
              </div>
              <p className="text-sm text-muted-foreground">8.5x increase in routing table size. Layer 3 (Network Layer) issue.</p>
            </div>
            
            <div className="slide-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-xs font-mono text-primary">2</span>
                </div>
                <span className="font-heading font-semibold text-foreground">Hypothesis Generation</span>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <span className="text-accent">a)</span> BGP route leak from peer AS
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-muted-foreground/50">b)</span> BGP hijack attack
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-muted-foreground/50">c)</span> Internal misconfiguration
                </li>
              </ul>
            </div>
            
            <div className="slide-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-xs font-mono text-primary">3</span>
                </div>
                <span className="font-heading font-semibold text-foreground">Evidence Evaluation</span>
              </div>
              <div className="text-sm space-y-2">
                <p className="text-muted-foreground">
                  <span className="text-accent">✓</span> Routes originated from AS65001
                </p>
                <p className="text-muted-foreground">
                  <span className="text-accent">✓</span> No prefix filter on peer session
                </p>
                <p className="text-muted-foreground">
                  <span className="text-accent">✓</span> Recent config change matches timing
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="slide-card border-accent/30">
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-5 h-5 text-accent" />
                <span className="font-heading font-semibold text-foreground">Root Cause</span>
              </div>
              <p className="text-foreground">BGP route leak from AS65001</p>
              <div className="mt-2 flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Confidence:</span>
                <span className="font-mono text-accent">95%</span>
              </div>
            </div>
            
            <div className="slide-card border-primary/30">
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="w-5 h-5 text-primary" />
                <span className="font-heading font-semibold text-foreground">Solution</span>
              </div>
              <div className="space-y-2 text-sm font-mono text-muted-foreground">
                <p className="text-primary"># Apply strict prefix filter</p>
                <p>ip prefix-list AS65001-INBOUND permit 203.0.113.0/24</p>
                <p>ip prefix-list AS65001-INBOUND deny 0.0.0.0/0 le 32</p>
                <p className="mt-2 text-primary"># Soft reset BGP</p>
                <p>clear bgp ipv4 unicast 10.1.1.2 soft in</p>
              </div>
            </div>
            
            <div className="slide-card">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-secondary" />
                <span className="font-heading font-semibold text-foreground">Prevention</span>
              </div>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Implement IRR-based filter generation</li>
                <li>• Add max-prefix limit</li>
                <li>• Enable RPKI validation</li>
              </ul>
            </div>
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default NOCApplicationsSlide;
